import { getDocument, GlobalWorkerOptions, version as pdfjsVersion } from 'pdfjs-dist';

/**
 * Do NOT use Vite `?url` hashed /assets/pdf.worker-*.mjs — production often 404s that chunk
 * ("Failed to fetch dynamically imported module"). Use public/pdf.worker.min.mjs + CDN fallback.
 * nginx must serve .mjs as application/javascript (not octet-stream).
 */
const LOCAL_WORKER = `${import.meta.env.BASE_URL || '/'}pdf.worker.min.mjs`;
const CDN_WORKER = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.mjs`;

GlobalWorkerOptions.workerSrc = LOCAL_WORKER;

const MAX_EDGE = 2400;
const MAX_BYTES = 8 * 1024 * 1024; // stay under default upload limit

export function isPdfFile(file) {
  if (!file) return false;
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
}

export function isFloorPlanImageFile(file) {
  if (!file) return false;
  return file.type.startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(file.name || '');
}

function isPdfWorkerError(err) {
  const msg = String(err?.message || err || '');
  return (
    /fake worker|dynamically imported module|pdf\.worker|Setting up fake worker/i.test(msg) ||
    err?.name === 'UnknownErrorException'
  );
}

async function renderPage(data, pageNumber, scale) {
  const pdf = await getDocument({ data }).promise;
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  return { canvas, numPages: pdf.numPages };
}

/** Render one PDF page to canvas (high-res for Excel printouts). */
export async function renderPdfPageToCanvas(file, pageNumber = 1, scale = 2) {
  const data = await file.arrayBuffer();
  // Fresh ArrayBuffer copy for retry (getDocument may detach the first buffer)
  const copy = () => data.slice(0);

  GlobalWorkerOptions.workerSrc = LOCAL_WORKER;
  try {
    return await renderPage(copy(), pageNumber, scale);
  } catch (e) {
    if (!isPdfWorkerError(e)) throw e;
    console.warn('[floorPlanPdf] local worker failed, trying CDN', e);
    GlobalWorkerOptions.workerSrc = CDN_WORKER;
    return await renderPage(copy(), pageNumber, scale);
  }
}

export function cropCanvas(source, x, y, width, height) {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(source, Math.round(x), Math.round(y), w, h, 0, 0, w, h);
  return out;
}

/** Downscale so the longest edge is <= maxEdge. */
export function fitCanvas(source, maxEdge = MAX_EDGE) {
  const w = source.width;
  const h = source.height;
  if (!w || !h) return source;
  const longest = Math.max(w, h);
  if (longest <= maxEdge) return source;
  const scale = maxEdge / longest;
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(w * scale));
  out.height = Math.max(1, Math.round(h * scale));
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(source, 0, 0, out.width, out.height);
  return out;
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Failed to encode image'));
        else resolve(blob);
      },
      type,
      quality
    );
  });
}

/**
 * Encode floor-plan canvas as a File under the upload size limit.
 * Prefers JPEG for large pages.
 */
export async function canvasToPngFile(canvas, filename = 'floor-plan.png') {
  const fitted = fitCanvas(canvas, MAX_EDGE);
  const base = String(filename || 'floor-plan').replace(/\.(png|jpe?g|webp)$/i, '');

  for (const q of [0.88, 0.78, 0.68, 0.55]) {
    try {
      const blob = await canvasToBlob(fitted, 'image/jpeg', q);
      if (blob.size <= MAX_BYTES) {
        return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
      }
    } catch {
      /* try next */
    }
  }

  const smaller = fitCanvas(fitted, 1600);
  const blob = await canvasToBlob(smaller, 'image/jpeg', 0.55);
  if (blob.size > MAX_BYTES) {
    throw new Error(
      `Image still too large (${Math.round(blob.size / 1024 / 1024)} MB). Zoom in and select a smaller area.`
    );
  }
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}

/** Compress a user-picked image File if oversized. */
export async function prepareFloorPlanImageFile(file) {
  if (!file || !isFloorPlanImageFile(file)) return file;
  if (file.size <= MAX_BYTES && file.size < 3 * 1024 * 1024) return file;
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0);
  bitmap.close?.();
  const base = String(file.name || 'floor-plan').replace(/\.(png|jpe?g|webp|gif)$/i, '');
  return canvasToPngFile(canvas, `${base}.jpg`);
}
